import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  collection,
  doc,
  getDocs,
  onSnapshot
} from 'firebase/firestore';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { db, Student } from '../../../utils/firebase';
import { useClassKioskAccess } from '../../../hooks/useClassKioskAccess';
import Intersect from '../../../components/icons/Intersect';
import {
  getTeacherSeatCombinedScale,
  getTeacherSeatRenderSizes,
  getTeacherSeatViewportScale
} from '../../../utils/seatBoardLayout';
import {
  countAbsentStudents,
  DailyAttendanceStatus,
  getAttendanceDateKey,
  getDailyAttendancePath,
  isStudentAbsent
} from '../../../utils/classAttendance';
import { toggleStudentAbsentStatus } from '../../../utils/classAttendanceApi';

interface Seat {
  studentId?: string | null;
  studentName?: string | null;
  name?: string | null;
  uid?: string | null;
  isDisabled?: boolean;
  position: { row: number; col: number };
  groupId?: string;
}

interface SeatingSettings {
  rows: number;
  cols: number;
  boardPosition: 'top' | 'bottom';
  layoutTemplate?: string;
  startDate?: Date | null;
  endDate?: Date | null;
}

interface SeatingArrangement {
  seats: Record<string, Seat>;
  settings: SeatingSettings;
  status: string;
  updatedAt?: Date;
}

const firestoreDate = (v: unknown, fallback: Date | null = null): Date | null => {
  if (v != null && typeof (v as { toDate?: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  if (v instanceof Date) return v;
  return fallback;
};

const getSeatOccupantId = (seat: Seat | undefined): string | null => {
  if (!seat) return null;
  const id = seat.studentId ?? seat.uid;
  if (id == null || String(id).trim() === '') return null;
  return String(id).trim();
};

const SeatDisplay: React.FC = () => {
  const { access, isKioskMode, authReady, authError, disconnect } = useClassKioskAccess();
  const boardFitAreaRef = useRef<HTMLDivElement | null>(null);

  const [students, setStudents] = useState<Student[]>([]);
  const [arrangement, setArrangement] = useState<SeatingArrangement | null>(null);
  const [attendanceRecords, setAttendanceRecords] = useState<Record<string, DailyAttendanceStatus>>({});
  const [attendanceSavingId, setAttendanceSavingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const todayDateKey = useMemo(() => getAttendanceDateKey(), []);
  const todayLabel = useMemo(
    () => format(new Date(), 'yyyy년 M월 d일 (EEE)', { locale: ko }),
    []
  );

  const [teacherViewportScale, setTeacherViewportScale] = useState(() =>
    typeof window !== 'undefined'
      ? getTeacherSeatViewportScale(window.innerWidth, window.innerHeight)
      : 1
  );
  const [boardFitAreaSize, setBoardFitAreaSize] = useState({ w: 0, h: 0 });

  const gridRows = arrangement?.settings.rows ?? 5;
  const gridCols = arrangement?.settings.cols ?? 6;
  const boardPosition = arrangement?.settings.boardPosition ?? 'top';

  useEffect(() => {
    const update = () => {
      setTeacherViewportScale(
        getTeacherSeatViewportScale(window.innerWidth, window.innerHeight)
      );
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useLayoutEffect(() => {
    const el = boardFitAreaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setBoardFitAreaSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [arrangement?.settings.rows, arrangement?.settings.cols]);

  const teacherCombinedScale = useMemo(() => {
    if (boardFitAreaSize.w < 80 || boardFitAreaSize.h < 80) {
      return teacherViewportScale;
    }
    return getTeacherSeatCombinedScale({
      availWidth: boardFitAreaSize.w,
      availHeight: boardFitAreaSize.h,
      gridCols,
      gridRows,
      viewportComfortScale: teacherViewportScale,
      // 태블릿/표시 전용: 세로까지 맞춰 잘림 없이 한 화면에 보이게
      fitHeight: true
    });
  }, [boardFitAreaSize, gridCols, gridRows, teacherViewportScale]);

  const seatSizes = useMemo(
    () => getTeacherSeatRenderSizes(teacherCombinedScale),
    [teacherCombinedScale]
  );

  useEffect(() => {
    if (!access?.schoolId || !access?.classId) {
      if (authReady) {
        setLoading(false);
      }
      return;
    }

    const { schoolId, classId } = access;
    let cancelled = false;

    const loadStudents = async () => {
      try {
        const studentsRef = collection(db, `schools/${schoolId}/classes/${classId}/students`);
        const snapshot = await getDocs(studentsRef);
        if (cancelled) return;
        const studentsData = snapshot.docs.map((studentDoc) => {
          const data = studentDoc.data() as Record<string, unknown>;
          const rawNum = data.number;
          const num =
            rawNum === null || rawNum === undefined || rawNum === ''
              ? 0
              : typeof rawNum === 'number'
                ? rawNum
                : Number(rawNum);
          return {
            uid: studentDoc.id,
            ...data,
            number: Number.isFinite(num) ? num : 0
          } as Student;
        });
        setStudents(studentsData);
      } catch (err) {
        console.error('학생 목록 로드 오류:', err);
        if (!cancelled) {
          setError('학생 목록을 불러오는 중 오류가 발생했습니다.');
        }
      }
    };

    void loadStudents();

    const arrangementRef = doc(
      db,
      `schools/${schoolId}/classes/${classId}/seatingArrangements`,
      'current'
    );

    const unsubscribe = onSnapshot(
      arrangementRef,
      (snapshot) => {
        if (cancelled) return;
        if (!snapshot.exists()) {
          setArrangement(null);
          setLoading(false);
          return;
        }

        const data = snapshot.data();
        setArrangement({
          seats: (data.seats as Record<string, Seat>) || {},
          settings: {
            rows: data.settings?.rows || 5,
            cols: data.settings?.cols || 6,
            boardPosition: data.settings?.boardPosition === 'bottom' ? 'bottom' : 'top',
            layoutTemplate: data.settings?.layoutTemplate,
            startDate: firestoreDate(data.settings?.startDate),
            endDate: firestoreDate(data.settings?.endDate)
          },
          status: data.status || 'draft',
          updatedAt: firestoreDate(data.updatedAt) ?? undefined
        });
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('자리표 실시간 구독 오류:', err);
        if (!cancelled) {
          setError('자리표를 불러오는 중 오류가 발생했습니다.');
          setLoading(false);
        }
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [access?.schoolId, access?.classId, authReady]);

  useEffect(() => {
    if (!access?.schoolId || !access?.classId) return;

    const attendanceRef = doc(
      db,
      getDailyAttendancePath(access.schoolId, access.classId, todayDateKey)
    );

    const unsubscribe = onSnapshot(
      attendanceRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setAttendanceRecords({});
          return;
        }
        const data = snapshot.data();
        setAttendanceRecords((data.records as Record<string, DailyAttendanceStatus>) || {});
      },
      (err) => {
        console.error('출결 실시간 구독 오류:', err);
      }
    );

    return () => unsubscribe();
  }, [access?.schoolId, access?.classId, todayDateKey]);

  const absentCount = useMemo(() => countAbsentStudents(attendanceRecords), [attendanceRecords]);

  const toggleStudentAbsent = useCallback(
    async (studentId: string) => {
      if (!access?.schoolId || !access?.classId || !access.actorUid) return;
      if (attendanceSavingId) return;

      setAttendanceSavingId(studentId);
      try {
        await toggleStudentAbsentStatus({
          schoolId: access.schoolId,
          classId: access.classId,
          dateKey: todayDateKey,
          studentId,
          records: attendanceRecords,
          actorUid: access.actorUid
        });
      } catch (err) {
        console.error('출결 저장 오류:', err);
        setError('출결 저장 중 오류가 발생했습니다.');
      } finally {
        setAttendanceSavingId(null);
      }
    },
    [access, attendanceRecords, attendanceSavingId, todayDateKey]
  );

  const getLatestStudentName = (studentId: string | null, fallbackName?: string | null) => {
    if (!studentId) return fallbackName ?? null;
    const sid = String(studentId).trim();
    const matchedStudent = students.find((student) => String(student.uid).trim() === sid);
    return matchedStudent?.name ?? fallbackName ?? null;
  };

  const getSeatPositionLabel = (row: number, col: number, seat: Seat | undefined) => {
    const seatCode = String.fromCharCode(65 + row) + String(col + 1).padStart(2, '0');
    const occId = getSeatOccupantId(seat);
    if (!occId) return seatCode;

    const matched = students.find((s) => String(s.uid).trim() === occId);
    if (!matched) return seatCode;

    const raw = matched.number as unknown;
    const num =
      raw === null || raw === undefined || raw === ''
        ? NaN
        : typeof raw === 'number'
          ? raw
          : Number(raw);
    if (Number.isFinite(num) && num > 0) {
      return `${Math.floor(num)}번`;
    }
    return seatCode;
  };

  const renderBoardSection = () => (
    <div
      style={{
        width: '100%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        marginTop: '12px',
        marginBottom: '12px'
      }}
    >
      <div
        style={{
          width: '100%',
          textAlign: 'center',
          color: 'var(--Color-text-minor, #BABABA)',
          fontSize: `${seatSizes.px(14)}px`,
          fontFamily: 'var(--font-family-primary, Pretendard)',
          fontWeight: 600,
          lineHeight: `${seatSizes.px(21)}px`
        }}
      >
        칠판
      </div>
      <div
        style={{
          width: '100%',
          maxWidth: `${seatSizes.px(500)}px`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Intersect width={seatSizes.px(500)} height={seatSizes.px(38)} />
      </div>
    </div>
  );

  const renderSeatGrid = () => {
    if (!arrangement) return null;

    const CELL_SIZE = seatSizes.cellPx;
    const CHIP_PX = seatSizes.chipPx;
    const CHIP_RADIUS = seatSizes.chipBorderRadiusPx;
    const spx = seatSizes.px;
    const cellBox: React.CSSProperties = {
      width: `${CELL_SIZE}px`,
      height: `${CELL_SIZE}px`,
      minWidth: `${CELL_SIZE}px`,
      maxWidth: `${CELL_SIZE}px`,
      minHeight: `${CELL_SIZE}px`,
      maxHeight: `${CELL_SIZE}px`,
      overflow: 'hidden',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxSizing: 'border-box'
    };

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridCols}, ${CELL_SIZE}px)`,
          gridTemplateRows: `repeat(${gridRows}, ${CELL_SIZE}px)`,
          width: 'max-content',
          flexShrink: 0,
          padding: '12px',
          boxSizing: 'border-box'
        }}
      >
        {Array.from({ length: gridRows * gridCols }, (_, index) => {
          const row = Math.floor(index / gridCols);
          const col = index % gridCols;
          const seatKey = `${row}-${col}`;
          const seat = arrangement.seats[seatKey];

          if (!seat) {
            return <div key={seatKey} aria-hidden style={cellBox} />;
          }

          const occupantId = getSeatOccupantId(seat);
          const hasOccupant = Boolean(occupantId);
          const isAbsent = isStudentAbsent(attendanceRecords, occupantId);
          const isSavingAttendance = Boolean(occupantId && attendanceSavingId === occupantId);
          const seatDisplayName = getLatestStudentName(
            occupantId,
            seat.studentName ?? seat.name ?? null
          );

          const chipBackground = seat.isDisabled
            ? '#EDEDED'
            : isAbsent
              ? '#F3F3F3'
              : hasOccupant
                ? '#FFF1E7'
                : '#F8F8F8';

          return (
            <div key={seatKey} style={cellBox}>
              <button
                type="button"
                disabled={!hasOccupant || seat.isDisabled || isSavingAttendance}
                aria-label={
                  hasOccupant && seatDisplayName
                    ? isAbsent
                      ? `${seatDisplayName} 결석 해제`
                      : `${seatDisplayName} 결석 처리`
                    : undefined
                }
                onClick={() => {
                  if (occupantId && hasOccupant && !seat.isDisabled) {
                    void toggleStudentAbsent(occupantId);
                  }
                }}
                style={{
                  width: `${CHIP_PX}px`,
                  height: `${CHIP_PX}px`,
                  minWidth: `${CHIP_PX}px`,
                  maxWidth: `${CHIP_PX}px`,
                  padding: 0,
                  border: isAbsent
                    ? `2px solid #BABABA`
                    : hasOccupant
                      ? '2px solid transparent'
                      : 'none',
                  background: chipBackground,
                  borderRadius: `${CHIP_RADIUS}px`,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'hidden',
                  cursor:
                    hasOccupant && !seat.isDisabled
                      ? isSavingAttendance
                        ? 'wait'
                        : 'pointer'
                      : 'default',
                  opacity: isSavingAttendance ? 0.7 : 1,
                  boxSizing: 'border-box'
                }}
              >
                <div
                  style={{
                    fontSize: `${spx(14)}px`,
                    fontWeight: 600,
                    fontFamily: 'Pretendard, sans-serif',
                    lineHeight: `${spx(18)}px`,
                    textAlign: 'center',
                    maxWidth: `${spx(64)}px`,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: isAbsent ? '#9E9E9E' : '#4C4C4C',
                    fontVariantNumeric: 'tabular-nums',
                    textDecoration: isAbsent ? 'line-through' : 'none'
                  }}
                >
                  {getSeatPositionLabel(row, col, seat)}
                </div>
                {seatDisplayName && (
                  <div
                    style={{
                      fontSize: `${spx(13)}px`,
                      color: isAbsent ? '#9E9E9E' : '#4C4C4C',
                      fontFamily: 'Pretendard',
                      fontWeight: 500,
                      marginTop: '1px',
                      lineHeight: `${spx(17)}px`,
                      textAlign: 'center',
                      maxWidth: `${spx(64)}px`,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textDecoration: isAbsent ? 'line-through' : 'none'
                    }}
                  >
                    {seatDisplayName}
                  </div>
                )}
                {isAbsent && (
                  <div
                    style={{
                      fontSize: `${spx(10)}px`,
                      color: '#757575',
                      fontFamily: 'Pretendard',
                      fontWeight: 600,
                      marginTop: '2px',
                      lineHeight: `${spx(14)}px`
                    }}
                  >
                    결석
                  </div>
                )}
                {seat.isDisabled && (
                  <div
                    style={{
                      fontSize: `${spx(9)}px`,
                      color: '#BABABA',
                      fontFamily: 'Pretendard',
                      fontWeight: 400
                    }}
                  >
                    비활성화
                  </div>
                )}
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  if (!authReady) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-family-primary, Pretendard)'
        }}
      >
        연동 확인 중…
      </div>
    );
  }

  if (!access) {
    return <Navigate to="/class/kiosk/connect" replace />;
  }

  const periodLabel =
    arrangement?.settings.startDate && arrangement?.settings.endDate
      ? `${format(arrangement.settings.startDate, 'yyyy.MM.dd')} ~ ${format(arrangement.settings.endDate, 'yyyy.MM.dd')}`
      : '현재 자리표';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--Color-background-normal, white)',
        fontFamily: 'var(--font-family-primary, Pretendard)',
        overflow: 'hidden'
      }}
    >
      <header
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          padding: '16px 24px',
          borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span
            style={{
              fontSize: '14px',
              color: 'var(--Color-text-normal, #161616)',
              lineHeight: '21px'
            }}
          >
            {access.grade}학년 {access.classNumber}반 · {todayLabel}
          </span>
          <span
            style={{
              fontSize: '24px',
              fontWeight: 600,
              color: 'var(--Color-text-strong, #0D0D0D)',
              lineHeight: '36px'
            }}
          >
            {periodLabel}
          </span>
          <span
            style={{
              fontSize: '13px',
              color: 'var(--Color-text-normal, #161616)',
              lineHeight: '20px'
            }}
          >
            학생 칩을 눌러 결석 표시 · 오늘 결석 {absentCount}명
            {isKioskMode ? ' · 태블릿 연동 중' : ''}
          </span>
        </div>
        {isKioskMode ? (
          <button
            type="button"
            onClick={() => void disconnect()}
            style={{
              fontSize: '14px',
              color: 'var(--Color-text-normal, #161616)',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid var(--Color-line-normal, #F0F0F0)',
              background: 'var(--Color-background-normal, white)',
              cursor: 'pointer'
            }}
          >
            연동 해제
          </button>
        ) : (
          <Link
            to="/class/teacher/SeatManagement"
            style={{
              fontSize: '14px',
              color: 'var(--Color-text-normal, #161616)',
              textDecoration: 'none',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid var(--Color-line-normal, #F0F0F0)',
              background: 'var(--Color-background-normal, white)'
            }}
          >
            자리 관리로 돌아가기
          </Link>
        )}
      </header>

      {authError && (
        <div
          role="alert"
          style={{
            flexShrink: 0,
            padding: '12px 24px',
            background: '#FFF5F5',
            color: '#C62828',
            fontSize: '14px'
          }}
        >
          {authError}
        </div>
      )}

      <main
        ref={boardFitAreaRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          padding: '8px 16px 24px',
          boxSizing: 'border-box'
        }}
      >
        {/*
          width/height 100% + min-content: 작을 때는 가운데, 넘치면 스크롤 영역이 늘어나
          flex center만 쓸 때 생기던 좌/상단 잘림을 피한다.
        */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'safe center',
            minWidth: 'min-content',
            minHeight: '100%',
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          {loading && (
            <div style={{ fontSize: '16px', color: 'var(--Color-text-normal, #161616)' }}>
              자리표를 불러오는 중…
            </div>
          )}

          {!loading && error && (
            <div style={{ fontSize: '16px', color: '#D32F2F' }}>{error}</div>
          )}

          {!loading && !error && !arrangement && (
            <div
              style={{
                fontSize: '16px',
                color: 'var(--Color-text-normal, #161616)',
                textAlign: 'center'
              }}
            >
              아직 등록된 현재 자리표가 없습니다.
              <br />
              자리 관리에서 자리를 배치해 주세요.
            </div>
          )}

          {!loading && !error && arrangement && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flexShrink: 0
              }}
            >
              {boardPosition === 'top' && renderBoardSection()}
              {renderSeatGrid()}
              {boardPosition === 'bottom' && renderBoardSection()}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SeatDisplay;
